import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  useTheme,
  Checkbox,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  GetApp as GetAppIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { DashboardServices, apiCategory, reportsServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { Loader } from "../../components/CustomLoader";
import "../../styles/styles.css";
import { capitalizeFirstLetter } from "../../utils/helpers";

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && <Box className="reports-tab-panel">{children}</Box>}
    </div>
  );
}

// KPI Card Component
function KpiCard({ title, value, subtitle, trend, color = "primary" }) {
  const theme = useTheme();
  const colorClass = `reports-kpi-card-${color}`;
  return (
    <Card className={`reports-kpi-card ${colorClass}`}>
      <Typography variant="body2" color="text.secondary" gutterBottom className="reports-kpi-title">
        {title}
      </Typography>
      <Typography variant="h4" component="div" fontWeight="fontWeightBold" className="reports-kpi-value">
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" className="reports-kpi-subtitle">
          {subtitle}
        </Typography>
      )}
      {trend && (
        <Box className="reports-kpi-trend-container">
          <Chip
            label={`${trend > 0 ? "↑" : trend < 0 ? "↓" : "→"} ${Math.abs(trend)}%`}
            size="small"
            sx={{
              backgroundColor: trend > 0 ? theme.palette.success.soft : trend < 0 ? theme.palette.error.soft : theme.palette.grey[100],
              color: trend > 0 ? theme.palette.success.dark : trend < 0 ? theme.palette.error.dark : theme.palette.text.secondary,
              fontWeight: 600,
              fontSize: "0.7rem",
            }}
          />
        </Box>
      )}
    </Card>
  );
}

// Filters Component
function Filters({ filters, setFilters, onApply, categories = [] }) {

  return (
    <Card className="reports-filter-card">
      <CardContent className="reports-filter-content">
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={filters.timePeriod === "custom" ? 2.4 : 2.5}>
            <FormControl fullWidth>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={filters.timePeriod || "monthly"}
                onChange={(e) => setFilters((s) => ({ ...s, timePeriod: e.target.value }))}
                label="Time Period"
                className="reports-filter-select"
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {filters.timePeriod === "custom" && (
            <>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  label="From Date"
                  type="date"
                  value={filters.dateFrom || ""}
                  onChange={(e) => setFilters((s) => ({ ...s, dateFrom: e.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  className="reports-filter-textfield"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  label="To Date"
                  type="date"
                  value={filters.dateTo || ""}
                  onChange={(e) => setFilters((s) => ({ ...s, dateTo: e.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  className="reports-filter-textfield"
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6} md={filters.timePeriod === "custom" ? 3.4 : 3.5}>
            <FormControl fullWidth>
              <InputLabel id="auction-type-label">Auction Type</InputLabel>
              <Select
                multiple
                value={filters.auctionType || []}
                onChange={(e) => setFilters((s) => ({ ...s, auctionType: e.target.value }))}
                label="Auction Type"
                labelId="auction-type-label"
                className="reports-filter-select"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
                sx={{
                  '& .MuiSelect-select': {
                    overflow: 'visible !important',
                    textOverflow: 'clip !important',
                    whiteSpace: 'nowrap !important',
                  }
                }}
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return <span style={{ color: '#64748B', display: 'block', overflow: 'visible', whiteSpace: 'nowrap' }}>All Auction Types</span>;
                  }
                  if (selected.length === 3) {
                    return <span style={{ display: 'block', overflow: 'visible', whiteSpace: 'nowrap' }}>All Auction Types</span>;
                  }
                  if (selected.length === 1) {
                    const typeMap = {
                      normal: "Normal",
                      marketDropAi: "MarketDropAI",
                      claimPoint: "ClaimPOINT"
                    };
                    return <span style={{ display: 'block', overflow: 'visible', whiteSpace: 'nowrap' }}>{typeMap[selected[0]] || selected[0]}</span>;
                  }
                  return <span style={{ display: 'block', overflow: 'visible', whiteSpace: 'nowrap' }}>{selected.length} Types Selected</span>;
                }}
              >
                <MenuItem value="normal">
                  <Checkbox checked={(filters.auctionType || []).includes("normal")} />
                  Normal
                </MenuItem>
                <MenuItem value="marketDropAi">
                  <Checkbox checked={(filters.auctionType || []).includes("marketDropAi")} />
                  MarketDropAI
                </MenuItem>
                <MenuItem value="claimPoint">
                  <Checkbox checked={(filters.auctionType || []).includes("claimPoint")} />
                  ClaimPOINT
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={filters.timePeriod === "custom" ? 2.4 : 2.5}>
            <FormControl fullWidth>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                multiple
                value={filters.categoryId || []}
                onChange={(e) => setFilters((s) => ({ ...s, categoryId: e.target.value }))}
                label="Category"
                labelId="category-label"
                className="reports-filter-select"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
                sx={{
                  '& .MuiSelect-select': {
                    overflow: 'visible !important',
                    textOverflow: 'clip !important',
                    whiteSpace: 'nowrap !important',
                  }
                }}
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return <span style={{ color: '#64748B', display: 'block', overflow: 'visible', whiteSpace: 'nowrap' }}>All Categories</span>;
                  }
                  const selectedNames = selected
                    .map((id) => categories.find((cat) => cat._id === id)?.name)
                    .filter(Boolean);
                  if (selectedNames.length === 0) {
                    return <span style={{ color: '#64748B', display: 'block', overflow: 'visible', whiteSpace: 'nowrap' }}>All Categories</span>;
                  }
                  if (selectedNames.length === 1) {
                    return <span style={{ display: 'block', overflow: 'visible', whiteSpace: 'nowrap' }}>{selectedNames[0]}</span>;
                  }
                  if (selectedNames.length > 3) {
                    return <span style={{ display: 'block', overflow: 'visible', whiteSpace: 'nowrap' }}>{selectedNames.length} Categories Selected</span>;
                  }
                  return <span style={{ display: 'block', overflow: 'visible', whiteSpace: 'nowrap' }}>{selectedNames.join(", ")}</span>;
                }}
              >
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    <Checkbox checked={(filters.categoryId || []).includes(category._id)} />
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={filters.timePeriod === "custom" ? 2.4 : 2.5} className="reports-filter-reset-button-container">
            <Button
              onClick={() => {
                setFilters({
                  timePeriod: "monthly",
                  dateFrom: "",
                  dateTo: "",
                  auctionType: [],
                  categoryId: [],
                });
                onApply && onApply();
              }}
              variant="outlined"
              fullWidth
              className="reports-filter-reset-button"
            >
              Reset Filters
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

// Trend Chart Component
function TrendChart({ data, dataKey, name, period, color = "#e96c0e" }) {
  const theme = useTheme();
  const chartColor = color === "#e96c0e" ? theme.palette.primary.main : color;
  
  // Determine XAxis dataKey based on data structure
  const getXAxisKey = () => {
    if (data && data.length > 0) {
      if (data[0].hour) return "hour";
      if (data[0].date) return "date";
      if (data[0].week) return "week";
      if (data[0].month) return "month";
    }
    return period === "daily" ? "date" : period === "weekly" ? "week" : "month";
  };
  
  return (
    <div style={{ width: "100%", height: 300 }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id={`color${name.replace(/\s/g, "")}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={chartColor} stopOpacity={0.8} />
              <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.grey[200]} />
          <XAxis
            dataKey={getXAxisKey()}
            stroke={theme.palette.text.secondary}
          />
          <YAxis stroke={theme.palette.text.secondary} />
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: `1px solid ${theme.palette.border.light}`,
            }}
          />
          <Legend />
          <Area
            type="monotone"
            dataKey={dataKey}
            name={name}
            stroke={chartColor}
            strokeWidth={2}
            fillOpacity={1}
            fill={`url(#color${name.replace(/\s/g, "")})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// Auction Performance Chart
function AuctionPerformanceChart({ data }) {
  const theme = useTheme();
  return (
    <div style={{ width: "100%", height: 350 }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.grey[200]} />
          <XAxis dataKey="period" stroke={theme.palette.text.secondary} />
          <YAxis stroke={theme.palette.text.secondary} />
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: `1px solid ${theme.palette.border.light}`,
            }}
          />
          <Legend />
          <Bar dataKey="Normal" name="Normal" fill={theme.palette.primary.main} radius={[8, 8, 0, 0]} />
          <Bar dataKey="MarketDropAI" name="MarketDropAI" fill={theme.palette.success.main} radius={[8, 8, 0, 0]} />
          <Bar dataKey="ClaimPOINT" name="ClaimPOINT" fill={theme.palette.warning.main} radius={[8, 8, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Email Share Dialog
function ShareReportDialog({ open, onClose, onShare, reportType }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleShare = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      await onShare(email, reportType);
      setEmail("");
      onClose();
    } catch (err) {
      errorHandler(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share Report via Email</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Email Address"
          type="email"
          fullWidth
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="reports-share-dialog-textfield"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleShare} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "Share"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Main Component
export default function ReportsAndAnalytics() {
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [currentReportType, setCurrentReportType] = useState("");
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    timePeriod: "monthly",
    dateFrom: "",
    dateTo: "",
    auctionType: [],
    categoryId: [],
  });

  // Data stores - mapped from dashboard API
  const [dashboardData, setDashboardData] = useState({
    kpis: { activeAuctions: 0, bidsPlaced: 0, totalSales: 0, activeUsers: 0 },
    trends: [],
  });
  const [auctionPerformance, setAuctionPerformance] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState([]);
  const [biddingActivity, setBiddingActivity] = useState({
    bidHistory: [],
    topBidders: [],
    peakTimes: [],
    trends: [],
  });
  const [revenueData, setRevenueData] = useState({
    breakdown: [],
    byType: [],
    byCategory: [],
    trends: [],
  });
  const [orderData, setOrderData] = useState({
    completed: 0,
    pending: 0,
    cancelled: 0,
    orders: [],
  });

  // Fetch categories for filter dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiCategory.getAll({
          pageNumber: 1,
          pageSize: 100,
          status: [1],
        });
        if (response?.status === 200) {
          setCategories(response?.data?.data?.categories || []);
        }
      } catch (err) {
        errorHandler(err);
      }
    };
    fetchCategories();
  }, []);

  // Fetch all data from dashboard API
  const fetchAllData = async () => {
    try {
      setLoading(true);
      // Build request body according to API specification
      const requestBody = {
        timePeriod: filters.timePeriod || "monthly",
        ...(filters.timePeriod === "custom" && {
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
        }),
        ...(filters.auctionType && filters.auctionType.length > 0 && {
          auctionType: filters.auctionType,
        }),
        ...(filters.categoryId && filters.categoryId.length > 0 && {
          categoryId: filters.categoryId,
        }),
      };
      const response = await DashboardServices.getData(requestBody);
      
      if (response?.status && response?.data?.data) {
        const data = response.data.data;
        
        // Map dashboard data to Reports & Analytics structure
        // Dashboard Overview Tab
        setDashboardData({
          kpis: {
            activeAuctions: data.activeAuctions?.total || 0,
            bidsPlaced: data.liveBids?.total || 0,
            totalSales: data.completedOrders?.revenue || 0,
            activeUsers: data.totalUsers?.total || 0,
          },
          trends: data.auctionPerformance?.map((item) => ({
            month: item.month || "N/A",
            activeAuctions: data.activeAuctions?.total  || 0,
            bidsPlaced: data.liveBids?.total || 0,
            totalSales: data.completedOrders?.revenue || 0,
            activeUsers: data.totalUsers?.total || 0,
          })) || [],
        });

        // Auction Performance Tab - Map auctionPerformance array
        setAuctionPerformance(
          data.auctionPerformance?.map((item) => ({
            period: item.month || item.period || "N/A",
            Normal: item.Normal || 0,
            MarketDropAI: item.MarketDropAI || 0,
            ClaimPOINT: item.ClaimPOINT || 0,
            totalCreated: (item.Normal || 0) + (item.MarketDropAI || 0) + (item.ClaimPOINT || 0),
            successRate: 0, 
            avgBidsPerAuction: 0, 
            avgWinningBid: 0,
            unsoldRate: 0, 
          })) || []
        );

        // Performance Metrics - Map performanceMetrics array
        setPerformanceMetrics(
          data.performanceMetrics?.map((item) => ({
            auctionType: item.auctionType || "N/A",
            totalCreated: item.totalCreated || 0,
            successRate: item.successRate || 0,
            avgBidsPerAuction: item.avgBidsPerAuction || 0,
            avgWinningBid: item.avgWinningBid || 0,
            unsoldRate: item.unsoldRate || 0,
          })) || []
        );

        // Bidding Activity Tab
        setBiddingActivity({
          bidHistory: data.bidActivity || [],
          topBidders: [], // Not available in API response
          peakTimes: data.bidActivity?.map((bid) => ({
            time: bid.hour || bid.time || "N/A",
            count: bid.bids || bid.count || 0,
          })) || [],
          trends: data.bidActivity?.map((bid) => ({
            hour: bid.hour || bid.time || "N/A",
            totalBids: bid.bids || bid.count || 0,
          })) || [],
        });

        const normalRevenue = data.activeAuctions?.normal || 0;
        const marketDropAIRevenue = data.activeAuctions?.marketDropAI || 0;
        const claimPOINTRevenue = data.activeAuctions?.claimPOINT || 0;
        const totalByType = normalRevenue + marketDropAIRevenue + claimPOINTRevenue;

        setRevenueData({
          breakdown: data.topCategories?.map((cat) => ({
            name: cat.name || "N/A",
            value: cat.revenue || 0,
          })) || [],
          byType: [
            {
              type: "Normal",
              revenue: normalRevenue,
              percentage: totalByType > 0 ? Math.round((normalRevenue / totalByType) * 100) : 0,
            },
            {
              type: "MarketDropAI",
              revenue: marketDropAIRevenue,
              percentage: totalByType > 0 ? Math.round((marketDropAIRevenue / totalByType) * 100) : 0,
            },
            {
              type: "ClaimPOINT",
              revenue: claimPOINTRevenue,
              percentage: totalByType > 0 ? Math.round((claimPOINTRevenue / totalByType) * 100) : 0,
            },
          ],
          byCategory: data.topCategories?.map((cat) => ({
            category: cat.name || "N/A",
            revenue: cat.revenue || 0,
            orders: cat.sales || 0,
            bids: cat.bids || 0,
            avgOrderValue: cat.sales > 0 && cat.revenue > 0 ? cat.revenue / cat.sales : 0,
          })) || [],
          trends: data.auctionPerformance?.map((item) => ({
            period: item.month || "N/A",
            revenue: data.completedOrders?.revenue || 0,
          })) || [],
        });

        setOrderData({
          completed: data.orderReports?.completed || 0,
          pending: data.orderReports?.pending || 0,
          cancelled: data.orderReports?.cancelled || 0,
          orders: data.orderDetails?.map((order) => ({
            _id: order._id,
            orderId: order.orderId || "-",
            auctionType: order.auctionType || order.auction?.auctionType || "-",
            auctionId: order.auction?.auctionId || "-",
            vehicleName: order.vehicle?.vehicleName || "N/A",
            buyerName: order.buyer?.name || "N/A",
            status: order.status || "N/A",
            finalPrice: order.finalPrice || 0,
            createdAt: order.createdAt || new Date(),
          })) || [],
        });
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setLoading(false);
    }
  };

  // Generate all tabs data for Excel
  const getAllTabsData = () => {
    const allTabsData = [];

    // Tab 0: Dashboard Overview
    const dashboardRows = [];
    dashboardRows.push(["Dashboard Overview Report"]);
    dashboardRows.push([`Generated on: ${format(new Date(), "yyyy-MM-dd HH:mm:ss")}`]);
    dashboardRows.push([]);
    dashboardRows.push(["KPIs", ""]);
    dashboardRows.push(["Active Auctions", dashboardData.kpis.activeAuctions]);
    dashboardRows.push(["Bids Placed", dashboardData.kpis.bidsPlaced]);
    dashboardRows.push(["Total Sales", `$${dashboardData.kpis.totalSales.toLocaleString()}`]);
    dashboardRows.push(["Active Users", dashboardData.kpis.activeUsers]);
    dashboardRows.push([]);
    dashboardRows.push(["Trends", ""]);
    dashboardRows.push(["Period", "Active Auctions", "Bids Placed", "Total Sales", "Active Users"]);
    dashboardData.trends.forEach((trend) => {
      dashboardRows.push([
        trend.month || trend.period || "N/A",
        trend.activeAuctions || 0,
        trend.bidsPlaced || 0,
        `$${(trend.totalSales || 0).toLocaleString()}`,
        trend.activeUsers || 0,
      ]);
    });
    allTabsData.push({ name: "Dashboard Overview", data: dashboardRows });

    // Tab 1: Auction Performance
    const auctionRows = [];
    auctionRows.push(["Auction Performance Report"]);
    auctionRows.push([`Generated on: ${format(new Date(), "yyyy-MM-dd HH:mm:ss")}`]);
    auctionRows.push([]);
    auctionRows.push(["Auction Performance by Period", ""]);
    auctionRows.push(["Period", "Normal", "MarketDropAI", "ClaimPOINT", "Total"]);
    auctionPerformance.forEach((r) => {
      auctionRows.push([
        r.period || "N/A",
        r.Normal || 0,
        r.MarketDropAI || 0,
        r.ClaimPOINT || 0,
        r.totalCreated || 0,
      ]);
    });
    auctionRows.push([]);
    auctionRows.push(["Performance Metrics", ""]);
    auctionRows.push(["Auction Type", "Total Created", "Success Rate", "Avg Bids/Auction", "Avg Winning Bid", "Unsold Rate"]);
    performanceMetrics.forEach((perf) => {
      auctionRows.push([
        perf.auctionType || "N/A",
        perf.totalCreated || 0,
        `${perf.successRate || 0}%`,
        perf.avgBidsPerAuction || 0,
        `$${(perf.avgWinningBid || 0).toLocaleString()}`,
        `${perf.unsoldRate || 0}%`,
      ]);
    });
    allTabsData.push({ name: "Auction Performance", data: auctionRows });

    // Tab 2: Bidding Activity
    const biddingRows = [];
    biddingRows.push(["Bidding Activity Report"]);
    biddingRows.push([`Generated on: ${format(new Date(), "yyyy-MM-dd HH:mm:ss")}`]);
    biddingRows.push([]);
    biddingRows.push(["Bid Activity by Hour", ""]);
    biddingRows.push(["Hour", "Bids"]);
    biddingActivity.bidHistory.forEach((bid) => {
      biddingRows.push([bid.hour || bid.time || "N/A", bid.bids || bid.count || 0]);
    });
    allTabsData.push({ name: "Bidding Activity", data: biddingRows });

    // Tab 3: Revenue Reports
    const revenueRows = [];
    revenueRows.push(["Revenue Reports"]);
    revenueRows.push([`Generated on: ${format(new Date(), "yyyy-MM-dd HH:mm:ss")}`]);
    revenueRows.push([]);
    revenueRows.push(["Top Categories", ""]);
    revenueRows.push(["Category", "Bids", "Sales", "Revenue"]);
    revenueData.byCategory.forEach((cat) => {
      revenueRows.push([
        cat.category || "N/A",
        cat.bids || 0,
        cat.orders || 0,
        `$${(cat.revenue || 0).toLocaleString()}`,
      ]);
    });
    revenueRows.push([]);
    revenueRows.push(["Revenue Summary", ""]);
    revenueRows.push(["Total Revenue", `$${(revenueData.breakdown.reduce((sum, item) => sum + (item.value || 0), 0)).toLocaleString()}`]);
    revenueRows.push(["Total Sales", revenueData.byCategory.reduce((sum, cat) => sum + (cat.orders || 0), 0)]);
    allTabsData.push({ name: "Revenue Reports", data: revenueRows });

    // Tab 4: Order Reports
    const orderRows = [];
    orderRows.push(["Order Reports"]);
    orderRows.push([`Generated on: ${format(new Date(), "yyyy-MM-dd HH:mm:ss")}`]);
    orderRows.push([]);
    orderRows.push(["Order Summary", ""]);
    orderRows.push(["Status", "Count"]);
    orderRows.push(["Completed", orderData.completed || 0]);
    orderRows.push(["Pending", orderData.pending || 0]);
    orderRows.push(["Cancelled", orderData.cancelled || 0]);
    orderRows.push([]);
    orderRows.push(["Order Details", ""]);
    orderRows.push(["Vehicle", "Auction Type", "Buyer", "Status", "Amount", "Date"]);
    orderData.orders.forEach((order) => {
      orderRows.push([
        order.vehicleName || "N/A",
        capitalizeFirstLetter(order.auctionType) || "N/A",
        order.buyerName || "N/A",
        order.status || "N/A",
        `$${(order?.finalPrice || 0).toLocaleString()}`,
        order.createdAt ? format(new Date(order.createdAt), "yyyy-MM-dd") : "N/A",
      ]);
    });
    allTabsData.push({ name: "Order Reports", data: orderRows });

    return allTabsData;
  };

  // Generate Excel file from all tabs data with formatting
  const generateExcelFile = () => {
    const wb = XLSX.utils.book_new();
    const allTabsData = getAllTabsData();

    allTabsData.forEach((tab) => {
      const ws = XLSX.utils.aoa_to_sheet(tab.data);
      
      // Set column widths for better readability
      const maxWidths = {};
      tab.data.forEach((row, rowIndex) => {
        if (Array.isArray(row)) {
          row.forEach((cell, colIndex) => {
            const cellValue = cell?.toString() || "";
            const currentWidth = maxWidths[colIndex] || 10;
            maxWidths[colIndex] = Math.max(currentWidth, Math.min(cellValue.length + 2, 50));
          });
        }
      });

      ws["!cols"] = Object.keys(maxWidths).map((key) => ({
        wch: maxWidths[key],
      }));

      // Add sheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, tab.name.substring(0, 31)); // Excel sheet name limit is 31 chars
    });

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    return new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  };

  const handleShareReport = async (email, reportType) => {
    try {
      const excelBlob = generateExcelFile();
      const fileName = `reports-all-tabs-${format(new Date(), "yyyyMMdd")}.xlsx`;
      
      const formData = new FormData();
      formData.append("email", email);
      formData.append("reportType", "all");
      formData.append("file", excelBlob, fileName);
      
      const response = await reportsServices.shareEmail(formData);
      
      if (response?.status === 200) {
        toast.success(response?.data?.message || "Report shared successfully via email!");
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [filters]);

  const reportTypeNames = ["dashboard", "auction", "bidding", "revenue", "orders"];

  return (
    <Box>
      {loading && <Loader />}
        {/* Header */}
        <Grid item xs={12}>
          <Paper elevation={0} className="reports-header">
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <Box className="reports-header-icon-container">
                  <AssessmentIcon className="reports-header-icon" />
                </Box>
                <Box>
                  <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom className="reports-header-title">
                    Reports & Analytics
                  </Typography>
                  <Typography variant="body2" className="reports-header-subtitle">
                    Comprehensive insights into platform performance, user activity, and financial trends
                  </Typography>
                </Box>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} className="reports-header-actions">
                <Button
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  onClick={() => {
                    const excelBlob = generateExcelFile();
                    const fileName = `reports-all-tabs-${format(new Date(), "yyyyMMdd")}.xlsx`;
                    const url = window.URL.createObjectURL(excelBlob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = fileName;
                    link.style.display = "none";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    toast.success("Excel file downloaded successfully with all tabs data!");
                  }}
                  className="reports-export-button"
                >
                  Export Excel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<EmailIcon />}
                  onClick={() => {
                    setCurrentReportType(reportTypeNames[tabValue]);
                    setShareDialogOpen(true);
                  }}
                  className="reports-share-button"
                >
                  Share via Email
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* Filters */}
        <Grid item xs={12}>
          <Filters
            filters={filters}
            setFilters={setFilters}
            categories={categories}
            onApply={() => {
              // Filters will trigger useEffect to refetch data
            }}
          />
        </Grid>

        {/* Tabs */}
        <Grid item xs={12}>
          <Card className="reports-tabs-card">
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              aria-label="reports tabs"
              className="reports-tabs-container"
            >
              <Tab label="Dashboard Overview" />
              <Tab label="Auction Performance" />
              <Tab label="Bidding Activity" />
              <Tab label="Revenue Reports" />
              <Tab label="Order Reports" />
            </Tabs>

            {/* Dashboard Overview Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box className="reports-tab-content">
                {/* KPIs */}
                <Grid container spacing={3} className="reports-kpi-grid">
                    <KpiCard
                      title="Active Auctions"
                      value={dashboardData.kpis.activeAuctions || 0}
                      subtitle="Currently running auctions"
                      color="primary"
                    />
                    <KpiCard
                      title="Bids Placed"
                      value={dashboardData.kpis.bidsPlaced || 0}
                      subtitle="Total bids in selected period"
                      color="info"
                    />
                    <KpiCard
                      title="Total Sales"
                      value={`$${(dashboardData.kpis.totalSales || 0).toLocaleString()}`}
                      subtitle="Revenue from completed auctions"
                      color="success"
                    />
                    <KpiCard
                      title="Active Users"
                      value={dashboardData.kpis.activeUsers || 0}
                      subtitle="Users active in period"
                      color="warning"
                    />
                </Grid>

                {/* Trend Charts */}
                <Grid container spacing={3}>
                    <Card className="reports-chart-card">
                      <CardContent className="reports-chart-content">
                        <Typography variant="h6" component="h2" mb={2} fontWeight={600} className="reports-chart-title">
                          Active Auctions Trend
                        </Typography>
                        <TrendChart
                          data={dashboardData.trends}
                          dataKey="activeAuctions"
                          name="Active Auctions"
                          period={filters.timePeriod || "monthly"}
                        />
                      </CardContent>
                    </Card>
                    <Card className="reports-chart-card">
                      <CardContent className="reports-chart-content">
                        <Typography variant="h6" component="h2" mb={2} fontWeight={600} className="reports-chart-title">
                          Bids Placed Trend
                        </Typography>
                        <TrendChart
                          data={dashboardData.trends}
                          dataKey="bidsPlaced"
                          name="Bids Placed"
                          period={filters.timePeriod || "monthly"}
                        />
                      </CardContent>
                    </Card>
                    <Card className="reports-chart-card">
                      <CardContent className="reports-chart-content">
                        <Typography variant="h6" component="h2" mb={2} fontWeight={600} className="reports-chart-title">
                          Sales Trend
                        </Typography>
                        <TrendChart
                          data={dashboardData.trends}
                          dataKey="totalSales"
                          name="Total Sales"
                          period={filters.timePeriod || "monthly"}
                        />
                      </CardContent>
                    </Card>
                    <Card className="reports-chart-card">
                      <CardContent className="reports-chart-content">
                        <Typography variant="h6" component="h2" mb={2} fontWeight={600} className="reports-chart-title">
                          Active Users Trend
                        </Typography>
                        <TrendChart
                          data={dashboardData.trends}
                          dataKey="activeUsers"
                          name="Active Users"
                          period={filters.timePeriod || "monthly"}
                        />
                      </CardContent>
                    </Card>
                </Grid>
              </Box>
            </TabPanel>

            {/* Auction Performance Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box className="reports-tab-content">
                <Grid className="reports-auction-performance-container">
                    <Card className="reports-chart-card">
                      <CardContent className="reports-chart-content">
                        <Typography variant="h6" component="h2" className="reports-chart-title">
                          Auction Performance by Type
                        </Typography>
                        <AuctionPerformanceChart data={auctionPerformance} />
                      </CardContent>
                    </Card>
                    <Card className="reports-chart-card">
                      <CardContent className="reports-chart-content">
                        <Typography variant="h6" component="h2" mb={2} fontWeight={600} className="reports-chart-title">
                          Performance Metrics
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" className="reports-table-container">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Auction Type</TableCell>
                                <TableCell align="right">Total Created</TableCell>
                                <TableCell align="right">Success Rate</TableCell>
                                <TableCell align="right">Avg Bids/Auction</TableCell>
                                <TableCell align="right">Avg Winning Bid</TableCell>
                                <TableCell align="right">Unsold Rate</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {performanceMetrics.length > 0 ? (
                                performanceMetrics.map((perf, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>
                                      <Chip
                                        label={perf.auctionType || "N/A"}
                                        color={perf.auctionType === "Normal" ? "primary" : perf.auctionType === "MarketDropAI" ? "success" : "warning"}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell align="right">{perf.totalCreated || 0}</TableCell>
                                    <TableCell align="right">{perf.successRate || 0}%</TableCell>
                                    <TableCell align="right">{perf.avgBidsPerAuction || 0}</TableCell>
                                    <TableCell align="right">${(perf.avgWinningBid || 0).toLocaleString()}</TableCell>
                                    <TableCell align="right">{perf.unsoldRate || 0}%</TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={6} align="center">
                                    No data available
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
              </Box>
            </TabPanel>

            {/* Bidding Activity Tab */}
            <TabPanel value={tabValue} index={2}>
              <Box className="reports-tab-content">
                <Grid container spacing={3}>
                    <Card className="reports-chart-card">
                      <CardContent className="reports-chart-content">
                        <Typography variant="h6" component="h2" mb={2} fontWeight={600} className="reports-chart-title">
                          Bidding Trends
                        </Typography>
                        {biddingActivity.trends.length > 0 ? (
                          <TrendChart
                            data={biddingActivity.trends}
                            dataKey="totalBids"
                            name="Total Bids"
                            period="daily"
                          />
                        ) : (
                          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
                            <Typography variant="body2" color="text.secondary">
                              No bidding activity data available
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                    <Card className="reports-chart-card">
                      <CardContent className="reports-chart-content">
                        <Typography variant="h6" component="h2" mb={2} fontWeight={600} className="reports-chart-title">
                          Peak Bidding Times
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" className="reports-table-container">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Time</TableCell>
                                <TableCell align="right">Bids</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {biddingActivity.peakTimes.length > 0 ? (
                                biddingActivity.peakTimes.map((peak, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{peak.time}</TableCell>
                                    <TableCell align="right">{peak.count}</TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={2} align="center">
                                    No data available
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                    <Card className="reports-chart-card">
                      <CardContent className="reports-chart-content">
                        <Typography variant="h6" component="h2" mb={2} fontWeight={600} className="reports-chart-title">
                          Bid Activity by Hour
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" className="reports-table-container">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Time</TableCell>
                                <TableCell align="right">Bids</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {biddingActivity.bidHistory.length > 0 ? (
                                biddingActivity.bidHistory.map((bid, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{bid.hour || bid.time || "N/A"}</TableCell>
                                    <TableCell align="right">{bid.bids || bid.count || 0}</TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={2} align="center">
                                    No bid history available
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                </Grid>
              </Box>
            </TabPanel>

            {/* Revenue Reports Tab */}
            <TabPanel value={tabValue} index={3}>
              <Box className="reports-tab-content">
                <Grid container spacing={3}>
                    <Card className="reports-chart-card">
                      <CardContent className="reports-chart-content">
                        <Typography variant="h6" component="h2" mb={2} fontWeight={600} className="reports-chart-title">
                          Revenue by Auction Type
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" className="reports-table-container">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Auction Type</TableCell>
                                <TableCell align="right">Revenue</TableCell>
                                <TableCell align="right">Percentage</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {revenueData.byType.length > 0 ? (
                                revenueData.byType.map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{item.type}</TableCell>
                                    <TableCell align="right">${(item.revenue || 0).toLocaleString()}</TableCell>
                                    <TableCell align="right">{item.percentage || 0}%</TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={3} align="center">
                                    No data available
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                    <Card className="reports-chart-card">
                      <CardContent className="reports-chart-content">
                        <Typography variant="h6" component="h2" mb={2} fontWeight={600} className="reports-chart-title">
                          Revenue Trend
                        </Typography>
                        <TrendChart
                          data={revenueData.trends}
                          dataKey="revenue"
                          name="Revenue"
                          period={filters.timePeriod || "monthly"}
                        />
                      </CardContent>
                    </Card>
                    <Card className="reports-chart-card">
                      <CardContent className="reports-chart-content">
                        <Typography variant="h6" component="h2" mb={2} fontWeight={600} className="reports-chart-title">
                          Revenue by Category
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" className="reports-table-container">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Category</TableCell>
                                <TableCell align="right">Revenue</TableCell>
                                <TableCell align="right">Orders</TableCell>
                                <TableCell align="right">Avg Order Value</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {revenueData.byCategory.length > 0 ? (
                                revenueData.byCategory.map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{item.category || "N/A"}</TableCell>
                                    <TableCell align="right">${(item.revenue || 0).toLocaleString()}</TableCell>
                                    <TableCell align="right">{item.orders || 0}</TableCell>
                                    <TableCell align="right">${(item.avgOrderValue || 0).toLocaleString()}</TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={4} align="center">
                                    No data available
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                </Grid>
              </Box>
            </TabPanel>

            {/* Order Reports Tab */}
            <TabPanel value={tabValue} index={4}>
              <Box className="reports-tab-content">
                <Grid container spacing={3}>
                    <KpiCard
                      title="Completed Orders"
                      value={orderData.completed || 0}
                      subtitle="Successfully delivered"
                      color="success"
                    />
                    <KpiCard
                      title="Pending Pickups"
                      value={orderData.pending || 0}
                      subtitle="Awaiting pickup"
                      color="warning"
                    />
                    <KpiCard
                      title="Cancelled Orders"
                      value={orderData.cancelled || 0}
                      subtitle="Cancelled by user/admin"
                      color="error"
                    />
                    <Card className="reports-chart-card reports-order-details-card">
                      <CardContent className="reports-chart-content">
                        <Typography variant="h6" component="h2" mb={2} fontWeight={600} className="reports-chart-title">
                          Order Details
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" className="reports-table-container">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Vehicle</TableCell>
                                <TableCell>Auction Type</TableCell>
                                <TableCell>Buyer</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Date</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {orderData.orders.length > 0 ? (
                                orderData.orders.map((order) => (
                                  <TableRow key={order._id}>
                                    <TableCell>{order.vehicleName || "N/A"}</TableCell>
                                    <TableCell>{capitalizeFirstLetter(order.auctionType) || "N/A"}</TableCell>
                                    <TableCell>{order.buyerName || "N/A"}</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={order.status || "N/A"}
                                        color={
                                          order.status === "Completed" || order.status === "Payment Completed"
                                            ? "success"
                                            : order.status === "Pending Payment" || order.status === "In Delivery"
                                              ? "warning"
                                              : order.status === "Cancelled"
                                                ? "error"
                                                : "default"
                                        }
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell>${(order?.finalPrice || 0).toLocaleString()}</TableCell>
                                    <TableCell>
                                      {order.createdAt ? format(new Date(order.createdAt), "yyyy-MM-dd") : "N/A"}
                                    </TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={7} align="center">
                                    No orders available
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                </Grid>
              </Box>
            </TabPanel>
          </Card>
        </Grid>

      {/* Share Report Dialog */}
      <ShareReportDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        onShare={handleShareReport}
        reportType={currentReportType}
      />
    </Box>
  );
}